import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message
} from 'antd';
import invoiceItemTemplateService, {
  InvoiceItemTemplate,
  CreateInvoiceItemTemplateInput
} from '../../../../services/invoiceItemTemplateService';
import { accountService } from '../../../../services/accountService';
import { useTranslation } from "react-i18next";

const { Option } = Select;

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: string;
}

interface InvoiceItemTemplateFormProps {
  visible: boolean;
  template?: InvoiceItemTemplate | null;
  onClose: () => void;
  onSuccess: () => void;
}

const InvoiceItemTemplateForm: React.FC<InvoiceItemTemplateFormProps> = ({
  visible,
  template,
  onClose,
  onSuccess
}) => {
    const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);

  useEffect(() => {
    if (visible) {
      loadCategories();
      loadAccounts();
      if (template) {
        // Edit mode
        form.setFieldsValue({
          itemCode: template.itemCode,
          description: template.description,
          category: template.category || undefined,
          unitPrice: Number(template.unitPrice),
          accountId: template.accountId || undefined
        });
      } else {
        // Create mode
        form.resetFields();
      }
    }
  }, [visible, template, form]);

  const loadCategories = async () => {
    try {
      const data = await invoiceItemTemplateService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountService.getAccounts();
      setAccounts(data);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  // Default Account picker shows all accounts (revenue, expense, AR, etc.)
  // — templates may legitimately point at any account (e.g. penalty fees,
  //   discounts, contra revenue), so we don't restrict by accountType anymore.
  // Kept under the original variable name for the JSX below; predicate
  // simply passes everything through.
  const revenueAccounts = accounts;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: CreateInvoiceItemTemplateInput = {
        itemCode: values.itemCode,
        description: values.description,
        category: values.category || undefined,
        unitPrice: values.unitPrice || 0,
        accountId: values.accountId || undefined
      };

      if (template) {
        // Update existing template
        await invoiceItemTemplateService.updateTemplate(template.id, data);
        message.success(t('Invoice item template updated successfully'));
      } else {
        // Create new template
        await invoiceItemTemplateService.createTemplate(data);
        message.success(t('Invoice item template created successfully'));
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(t('Failed to save template: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    if (newCategory && !categories.includes(newCategory)) {
      setCategories([...categories, newCategory]);
      form.setFieldValue('category', newCategory);
      setNewCategory('');
    }
  };

  return (
    <Modal
      title={template ? t('Edit Invoice Item Template') : t('Create Invoice Item Template')}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('Cancel')}
                      </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {template ? t('Update') : t('Create')}
        </Button>
      ]}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          unitPrice: 0
        }}
      >
        <Form.Item
          name="itemCode"
          label={t('Item Code')}
          rules={[
            { required: true, message: t('Please enter an item code') },
            {
              pattern: /^[a-zA-Z0-9_-]+$/,
              message: t('Item code can only contain letters, numbers, hyphens, and underscores')
            }
          ]}
        >
          <Input placeholder="e.g., CONSULTING-01, SERVICE-BASIC" />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('Description')}
          rules={[{ required: true, message: t('Please enter a description') }]}
        >
          <Input.TextArea
            rows={3}
            placeholder="e.g., Basic Consulting Service - Hourly Rate"
          />
        </Form.Item>

        <Form.Item
          name="category"
          label={t('Category (Optional)')}
        >
          <Select
            placeholder={t('Select or add a category')}
            allowClear
            showSearch
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                  <Input
                    style={{ flex: 'auto' }}
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onPressEnter={handleAddCategory}
                    placeholder={t('New category')}
                  />
                  <Button type="link" onClick={handleAddCategory}>
                    {t('Add')}
                                              </Button>
                </div>
              </>
            )}
          >
            {categories.map((cat) => (
              <Option key={cat} value={cat}>
                {cat}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="unitPrice"
          label={t('Default Unit Price')}
          rules={[{ required: true, message: t('Please enter a unit price') }]}
        >
          <InputNumber
            precision={2}
            prefix="$"
            style={{ width: '100%' }}
            placeholder="0.00"
          />
        </Form.Item>

        <Form.Item
          name="accountId"
          label={t('Default Account')}
          tooltip={t('The account to use when this item is added to an invoice')}
        >
          <Select
            placeholder={t('Select an account')}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              ((option?.children as any) || '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {revenueAccounts.map((account) => (
              <Option key={account.id} value={account.id}>
                {account.code} - {account.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InvoiceItemTemplateForm;
